/**
 * LedgerTab — grand livre d'un compte (solde d'ouverture, mouvements, solde courant) + PDF.
 * Batch #14.348 : période par défaut = mois en cours (raccourcis), colonnes triables (↑ / ↓) — le solde courant reste calculé chronologiquement.
 */
import { useCallback, useEffect, useState } from "react";
import { BookText, FileDown } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { DateRangeBar, SortTh, monthRange, useSort } from "@/components/ListTools";
import { AccountSelect, Btn, Empty, JOURNAL_CLS, Pill, downloadPdf, errMsg, money, useGlBase } from "./_shared";

export default function LedgerTab({ accounts, settings }) {
  const base = useGlBase();
  const [sp] = useSearchParams();
  const [accountId, setAccountId] = useState(sp.get("account") || "");
  const [range, setRange] = useState(() => (sp.get("from") || sp.get("to") ? { from: sp.get("from") || "", to: sp.get("to") || "" } : monthRange()));
  const { from, to } = range;
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const { sort, onSort, apply } = useSort("date", "asc");

  const load = useCallback(async () => {
    if (!accountId) { setData(null); return; }
    try {
      const r = await api.get(`${base}/ledger/${accountId}`, { params: { date_from: from || undefined, date_to: to || undefined } });
      setData(r.data);
    } catch (e) { toast.error(errMsg(e, "Grand livre indisponible")); }
  }, [accountId, from, to, base]);
  useEffect(() => { load(); }, [load]);

  const qs = `?${new URLSearchParams(Object.fromEntries(Object.entries({ date_from: from, date_to: to }).filter(([, v]) => v))).toString()}`;
  // Le serveur renvoie les lignes en ordre chronologique avec le solde courant ; on ne fait que réordonner l'affichage.
  const rows = data ? apply(data.rows.map((r, i) => ({ ...r, _i: i })), { date: (r) => `${r.date}|${String(r._i).padStart(6, "0")}` }) : [];
  const chronological = sort.key === "date" && sort.dir === "asc";

  return (
    <div className="space-y-4" data-testid="ledger-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <AccountSelect accounts={accounts} value={accountId} onChange={setAccountId} className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm w-80 bg-white" testId="ledger-account-select" />
          <DateRangeBar from={from} to={to} onChange={setRange} fyStartMonth={settings.fiscal_year_start_month || 1} testId="ledger-date" />
        </div>
        <Btn tone="slate" icon={FileDown} disabled={!data} busy={busy} onClick={() => downloadPdf(`${base}/ledger/${accountId}/pdf${qs}`, `GL_${data.account.number}.pdf`, setBusy)} testId="ledger-pdf-btn">PDF</Btn>
      </div>

      {!accountId && <Empty icon={BookText} text="Choisis un compte pour afficher son grand livre." testId="ledger-empty" />}

      {data && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between flex-wrap gap-2">
            <div><span className="font-mono text-xs text-slate-500">{data.account.number}</span> <span className="font-bold text-slate-800">{data.account.name}</span>
              <span className="ml-2 text-[11px] text-slate-500" data-testid="ledger-range-label">{from || "origine"} → {to || "aujourd'hui"} · {data.rows.length} mouvement(s)</span></div>
            <div className="text-xs text-slate-600 tabular-nums flex gap-4">
              <span>Ouverture <strong data-testid="ledger-opening">{money(data.opening_balance)}</strong></span>
              <span>Débits <strong>{money(data.total_debit)}</strong></span>
              <span>Crédits <strong>{money(data.total_credit)}</strong></span>
              <span>Clôture <strong className="text-emerald-700" data-testid="ledger-closing">{money(data.closing_balance)}</strong></span>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <SortTh label="Date" k="date" sort={sort} onSort={onSort} className="text-left px-3 py-2 w-24" testId="ledger-sort-date" />
                <SortTh label="N°" k="no" sort={sort} onSort={onSort} className="text-left px-2 py-2 w-14" testId="ledger-sort-no" />
                <SortTh label="Jrn" k="journal" sort={sort} onSort={onSort} className="text-left px-2 py-2 w-16" testId="ledger-sort-journal" />
                <SortTh label="Description" k="memo" sort={sort} onSort={onSort} className="text-left px-2 py-2" testId="ledger-sort-memo" />
                <SortTh label="Débit" k="debit" sort={sort} onSort={onSort} className="text-right px-2 py-2 w-28" align="right" testId="ledger-sort-debit" />
                <SortTh label="Crédit" k="credit" sort={sort} onSort={onSort} className="text-right px-2 py-2 w-28" align="right" testId="ledger-sort-credit" />
                <th className="text-right px-3 py-2 w-32">Solde</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {chronological && <tr className="text-slate-500 italic"><td className="px-3 py-2" colSpan={6}>Solde d'ouverture</td><td className="px-3 py-2 text-right font-mono tabular-nums">{money(data.opening_balance)}</td></tr>}
              {rows.map((r) => (
                <tr key={r._i} className="hover:bg-emerald-50/40" data-testid={`ledger-row-${r.no}`}>
                  <td className="px-3 py-2 font-mono text-xs">{r.date}</td>
                  <td className="px-2 py-2 font-mono text-xs">{r.no}</td>
                  <td className="px-2 py-2"><Pill cls={JOURNAL_CLS[r.journal]}>{r.journal}</Pill></td>
                  <td className="px-2 py-2"><div className="truncate max-w-md">{r.memo}</div>{r.description && r.description !== r.memo && <div className="text-[11px] text-slate-400 truncate max-w-md">{r.description}</div>}</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums">{r.debit ? money(r.debit) : ""}</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums">{r.credit ? money(r.credit) : ""}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold" title={chronological ? "" : "Solde courant à la date du mouvement (ordre chronologique)"}>{money(r.balance)}</td>
                </tr>
              ))}
              {!chronological && <tr className="text-slate-500 italic"><td className="px-3 py-2" colSpan={6}>Solde d'ouverture</td><td className="px-3 py-2 text-right font-mono tabular-nums">{money(data.opening_balance)}</td></tr>}
              {!data.rows.length && <tr><td colSpan={7}><Empty text="Aucun mouvement pour cette période — élargis la période (Trimestre, Exercice, Tout)." /></td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
